import React, { useState } from "react";
import { SITE } from "../config/siteConfig";
import { Layout } from "../components/Layout";
import { PhoneButton } from "../components/PhoneButtons";
import useSeo from "../utils/seo";

// Pages institutionnelles et légales (liens du pied de page), en espagnol.
// Textes : utiliser des guillemets doubles ou du JSX, jamais d'apostrophe dans une chaîne entre apostrophes.

// Sobre nosotros : qui édite le site, comment le service fonctionne.
export function AboutPage() {
  const c = SITE.company;
  const v = SITE.vertical;
  useSeo({
    title: "Quiénes somos",
    description: SITE.brandName + " es un servicio de comparación de ofertas " + v.product + " editado por " + c.name + ".",
    path: "/sobre-nosotros",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Quiénes somos</h1>

        <h2>¿Quién está detrás de {SITE.brandName}?</h2>
        <p>
          {SITE.brandName} ({SITE.siteLabel}) es un servicio de comparación de ofertas {v.product} para
          particulares. El sitio está editado por {c.name}, {c.form}, CIF {c.cif}, con domicilio social en {c.address}.{" "}
          {c.name} es una agencia de marketing digital especializada en poner en contacto a particulares con
          profesionales de la energía y las telecomunicaciones.
        </p>

        <h2>¿Qué hacemos?</h2>
        <p>
          Ayudamos a los particulares a comparar las ofertas {v.product} de {v.supplierSelection}. La comparación se
          hace por teléfono: las llamadas las atienden los asesores de {v.partner}, que estudian tu situación y te
          presentan las ofertas que mejor se adaptan a ti.
        </p>
        <p>
          {SITE.brandName} no es {v.notA}. No representamos a ninguna de las marcas citadas en este sitio y no
          gestionamos consultas sobre contratos existentes ({v.existingContract}): esas consultas deben dirigirse
          directamente a tu {v.supplierWord}.
        </p>

        <h2>¿Cómo nos financiamos?</h2>
        <p>
          El servicio de comparación es gratuito para ti. La llamada tiene el coste de una llamada a un fijo nacional,
          según tu operador. Recibimos una comisión de nuestro socio cuando se contrata una oferta, sin que afecte al
          precio que pagas.
        </p>

        <h2>Contacto</h2>
        <ul className="plain-list">
          <li>Asesores: {SITE.hours}</li>
          <li>
            Correo electrónico: <a href={"mailto:" + c.email}>{c.email}</a>
          </li>
          <li>Dirección: {c.address}</li>
        </ul>
        <div className="text-cta">
          <PhoneButton phone={SITE.phones.main} label="Comparar con un asesor" />
        </div>
      </article>
    </Layout>
  );
}

// Contacto : téléphone, e-mail, adresse, formulaire (mailto).
export function ContactPage() {
  const c = SITE.company;
  const v = SITE.vertical;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  useSeo({
    title: "Contacto",
    description: "Contactar con " + SITE.brandName + ": teléfono, correo electrónico, dirección y horario.",
    path: "/contacto",
  });

  // Le formulaire ouvre la messagerie du visiteur : aucune donnée n'est envoyée ni enregistrée par le site.
  function onSubmit(e) {
    e.preventDefault();
    const subject = "Contacto " + SITE.siteLabel + " - " + name;
    const body = "Nombre: " + name + "\nCorreo: " + email + "\n\n" + message;
    window.location.href =
      "mailto:" + c.email + "?subject=" + encodeURIComponent(subject) + "&body=" + encodeURIComponent(body);
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Contacto</h1>

        <div className="contact-grid">
          <div>
            <h2>Comparar ofertas por teléfono</h2>
            <p>Asesores disponibles {SITE.hours}.</p>
            <PhoneButton phone={SITE.phones.main} label="Comparar con un asesor" />
            <p className="small">Servicio de comparación gratuito · llamada a un fijo nacional según tu tarifa.</p>

            <h2>Titular del sitio</h2>
            <ul className="plain-list">
              <li>{c.name}</li>
              <li>CIF {c.cif}</li>
              <li>{c.address}</li>
              <li>Teléfono de administración: {c.phone}</li>
              <li>
                Correo electrónico: <a href={"mailto:" + c.email}>{c.email}</a>
              </li>
              <li>Respondemos a los correos {SITE.emailResponse}.</li>
            </ul>
            <p className="small">
              Para cualquier consulta sobre un contrato existente ({v.existingContract}), contacta directamente con tu{" "}
              {v.supplierWord}: no tenemos acceso a tu contrato.
            </p>
          </div>

          <form className="contact-form" onSubmit={onSubmit}>
            <h2>Escríbenos</h2>
            <label>
              Nombre
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name" />
            </label>
            <label>
              Correo electrónico
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              Mensaje
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} required />
            </label>
            <button type="submit" className="btn-submit">
              Enviar
            </button>
            <p className="small">
              El botón abre tu aplicación de correo con el mensaje ya escrito. Este sitio no guarda ningún dato.
            </p>
          </form>
        </div>
      </article>
    </Layout>
  );
}

// Preguntas frecuentes.
export function FAQPage() {
  const v = SITE.vertical;
  useSeo({
    title: "Preguntas frecuentes",
    description: "Respuestas a las preguntas frecuentes sobre el servicio de comparación " + SITE.brandName + ".",
    path: "/preguntas-frecuentes",
  });

  const items = [
    {
      q: "¿Quiénes sois?",
      a:
        SITE.brandName +
        " es un servicio de comparación de ofertas " +
        v.product +
        " editado por " +
        SITE.company.name +
        ", empresa española con sede en Barcelona.",
    },
    {
      q: v.areYouQuestion,
      a: "No. No somos " + v.notA + ", y no actuamos en nombre de ninguna de las marcas citadas en este sitio.",
    },
    {
      q: "¿El servicio es gratuito?",
      a:
        "Sí, la comparación es gratuita. La llamada tiene el coste de una llamada a un fijo nacional, según tu operador. Nuestro socio nos paga una comisión cuando se contrata una oferta, sin que afecte al precio que pagas.",
    },
    {
      q: "¿Quién me atiende por teléfono?",
      a: "Los asesores de " + v.partner + ". Están disponibles " + SITE.hours + ".",
    },
    {
      q: "¿Qué ofertas comparáis?",
      a:
        "Las ofertas de " +
        v.supplierSelection +
        ". La comparación no es exhaustiva y no incluye todas las ofertas del mercado.",
    },
    {
      q: "¿Puedo contactar con mi " + v.supplierWord + " actual a través de vuestro número?",
      a:
        "No. Para cualquier consulta sobre tu contrato actual (" +
        v.existingContract +
        "), contacta directamente con tu " +
        v.supplierWord +
        " a través de su web oficial, su área de clientes o los canales que figuran en tu factura.",
    },
    {
      q: "¿Estoy obligado a contratar?",
      a: "No. Eres libre de no contratar nada después de la comparación.",
    },
  ];

  if (SITE.emergency) {
    items.push({
      q: SITE.emergency.title,
      a:
        "No gestionamos averías ni urgencias. " +
        SITE.emergency.items.map((e) => e.label + ": contacta con " + e.value + ".").join(" "),
    });
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Preguntas frecuentes</h1>
        {items.map((it) => (
          <section key={it.q} className="faq-item">
            <h2>{it.q}</h2>
            <p>{it.a}</p>
          </section>
        ))}
        <div className="text-cta">
          <PhoneButton phone={SITE.phones.main} label="Comparar con un asesor" />
        </div>
      </article>
    </Layout>
  );
}

// Aviso importante : non-affiliation aux marques citées.
export function NoticePage() {
  const v = SITE.vertical;
  useSeo({
    title: "Aviso importante",
    description: "Aviso sobre la información publicada y las marcas citadas en " + SITE.siteLabel + ".",
    path: "/aviso",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Aviso importante</h1>
        <p>
          La información publicada en {SITE.siteLabel} es orientativa y no constituye asesoramiento personalizado ni
          una oferta contractual. Las características y los precios de las ofertas los comunican por teléfono los
          asesores de {v.partner} y pueden cambiar.
        </p>
        <p>
          {SITE.brandName} no es {v.notA}. {SITE.company.name} no está afiliada a ninguna de las marcas citadas en
          este sitio y no actúa en su nombre. Los nombres de las marcas se citan únicamente para que los visitantes
          puedan identificar la oferta que desean comparar; pertenecen a sus respectivos titulares.
        </p>
        <p>
          La comparación se basa en {v.supplierSelection} y no es exhaustiva. {SITE.company.name} recibe una comisión
          de su socio cuando se contrata una oferta, sin que afecte al precio que paga el cliente.
        </p>
        <p>
          Para cualquier consulta sobre un contrato existente ({v.existingContract}), contacta directamente con tu{" "}
          {v.supplierWord}.
        </p>
      </article>
    </Layout>
  );
}

// Aviso legal y condiciones de uso (LSSI-CE).
export function LegalPage() {
  const c = SITE.company;
  const v = SITE.vertical;
  useSeo({
    title: "Aviso legal y condiciones de uso",
    description: "Aviso legal y condiciones de uso del sitio " + SITE.siteLabel + ".",
    path: "/aviso-legal",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Aviso legal y condiciones de uso</h1>
        <p className="small">Última actualización: octubre de 2026</p>

        <h2>1. Titular del sitio</h2>
        <p>
          En cumplimiento del artículo 10 de la Ley 34/2002, de servicios de la sociedad de la información y de
          comercio electrónico (LSSI-CE), se informa de que el sitio {SITE.siteLabel} (el «Sitio») es titularidad de{" "}
          {c.name}, {c.form}, {c.register}, CIF {c.cif}, con domicilio social en {c.address}.
        </p>
        <ul className="plain-list">
          <li>Teléfono de administración: {c.phone}</li>
          <li>
            Correo electrónico: <a href={"mailto:" + c.email}>{c.email}</a>
          </li>
          <li>
            Protección de datos: <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a>
          </li>
        </ul>

        <h2>2. Definiciones</h2>
        <ul>
          <li>
            «Servicio»: la comparación gratuita de ofertas {v.product} y la puesta en contacto telefónica con nuestro
            socio.
          </li>
          <li>«Usuario»: toda persona mayor de edad que consulta el Sitio.</li>
          <li>
            «Socio»: papernest (Papernest Services S.A.S., Sucursal en España, NIF W2504279G), empresa tercera habilitada para comercializar las ofertas comparadas, a la que se dirigen las llamadas. Web:{" "}
            <a href="https://www.papernest.es" target="_blank" rel="noopener noreferrer">www.papernest.es</a>.
          </li>
        </ul>

        <h2>3. Objeto</h2>
        <p>
          Estas condiciones regulan el acceso al Sitio y su uso. No regulan los servicios prestados por el Socio ni{" "}
          {v.byTheSuppliers}, ni los contratos que el Usuario celebre con ellos.
        </p>

        <h2>4. Naturaleza del servicio</h2>
        <p>
          El Sitio presenta un servicio de comparación de ofertas {v.product}. La información publicada es
          orientativa y no constituye asesoramiento personalizado. {c.name} no es {v.notA}, no comercializa ninguna
          oferta en nombre propio y no gestiona ningún pago.
        </p>

        <h2>5. Puesta en contacto telefónica</h2>
        <p>
          El Sitio muestra un número de teléfono que dirige la llamada a los asesores del Socio, disponibles{" "}
          {SITE.hours}. No es un número de tarificación adicional: la llamada tiene el coste de una llamada a un fijo
          nacional, según el operador del Usuario. {c.name} no atiende las llamadas.
        </p>
        <p>
          La comparación se basa en {v.supplierSelection} y no es exhaustiva. {c.name} puede recibir una comisión del
          Socio cuando se contrata una oferta, sin que afecte al precio que paga el Usuario. El Socio y las empresas
          comparadas son libres de aceptar o rechazar una solicitud.
        </p>

        <h2>6. Marcas citadas</h2>
        <p>
          Los nombres de las marcas citadas en el Sitio se utilizan únicamente para que el Usuario pueda identificar
          la oferta que desea comparar. {c.name} no está afiliada a ninguna de ellas y no actúa en su nombre. Para
          cualquier consulta sobre un contrato existente, el Usuario debe contactar directamente con su{" "}
          {v.supplierWord}.
        </p>

        <h2>7. Acceso al Sitio</h2>
        <p>
          El Sitio está disponible de forma continua, salvo interrupciones necesarias para su mantenimiento o por
          causa de fuerza mayor. {c.name} puede modificar, suspender o interrumpir el acceso al Sitio sin previo aviso.
        </p>

        <h2>8. Responsabilidad</h2>
        <p>{c.name} no se hace responsable:</p>
        <ul>
          <li>de la exactitud o exhaustividad de la información facilitada por el Socio o las empresas comparadas;</li>
          <li>de la falta de disponibilidad temporal o definitiva de algunas ofertas;</li>
          <li>de las consecuencias de una decisión basada en información incompleta.</li>
        </ul>
        <p>El Usuario es el único responsable de la elección de su {v.supplierWord}.</p>

        <h2>9. Obligaciones del Usuario</h2>
        <p>
          El Usuario se compromete a utilizar el Sitio conforme a la ley, a no alterar su funcionamiento y a facilitar
          información veraz al Socio durante la llamada.
        </p>

        <h2>10. Propiedad intelectual e industrial</h2>
        <p>
          Los contenidos propios del Sitio (textos, diseño, logotipo {SITE.brandName}) están protegidos. Queda
          prohibida su reproducción sin autorización de {c.name}. Las marcas de terceros pertenecen a sus titulares.
        </p>

        <h2>11. Protección de datos y cookies</h2>
        <p>
          El tratamiento de los datos personales y el uso de cookies se describen en la{" "}
          <a href="/politica-de-privacidad">política de privacidad</a> y en la{" "}
          <a href="/cookies">política de cookies</a>.
        </p>

        <h2>12. Modificación de las condiciones</h2>
        <p>
          Estas condiciones pueden modificarse en cualquier momento. Se aplica la versión publicada en el momento de la
          consulta del Sitio.
        </p>

        <h2>13. Legislación aplicable y jurisdicción</h2>
        <p>
          Estas condiciones se rigen por la legislación española. Si el Usuario es consumidor, serán competentes los
          juzgados y tribunales de su domicilio; en los demás casos, los juzgados y tribunales de Barcelona.
        </p>

        <h2>14. Contacto</h2>
        <p>
          Para cualquier consulta sobre el Sitio o estas condiciones: <a href={"mailto:" + c.email}>{c.email}</a>
        </p>
      </article>
    </Layout>
  );
}

// Política de privacidad (RGPD y LOPDGDD).
export function PrivacyPage() {
  const c = SITE.company;
  useSeo({
    title: "Política de privacidad",
    description: "Datos tratados, finalidades, plazos de conservación y derechos de los usuarios de " + SITE.siteLabel + ".",
    path: "/politica-de-privacidad",
  });
  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Política de privacidad</h1>
        <p className="small">Última actualización: octubre de 2026</p>
        <p>
          Esta política se aplica de acuerdo con el Reglamento (UE) 2016/679 (RGPD) y la Ley Orgánica 3/2018, de
          Protección de Datos Personales y garantía de los derechos digitales (LOPDGDD).
        </p>

        <h2>1. Responsable del tratamiento</h2>
        <p>
          {c.name}, CIF {c.cif}, {c.address}. Contacto de protección de datos:{" "}
          <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a>
        </p>

        <h2>2. Datos tratados</h2>
        <p>El Sitio no tiene ningún formulario que guarde tus datos. Solo tratamos:</p>
        <ul>
          <li>
            datos técnicos de navegación (dirección IP, tipo de navegador y de dispositivo, páginas consultadas, página
            de procedencia);
          </li>
          <li>
            datos de seguimiento de llamadas: un identificador de sesión aleatorio, el identificador de medición de
            Google (_ga) si existe, la página consultada y la página de procedencia, asociados a la llamada realizada
            desde el Sitio (número de seguimiento marcado, fecha, hora y duración de la llamada);
          </li>
          <li>
            el contenido de los correos que nos envíes voluntariamente (el formulario de contacto solo abre tu
            aplicación de correo).
          </li>
        </ul>
        <p>No tratamos categorías especiales de datos en el sentido del artículo 9 del RGPD.</p>

        <h2>3. Finalidades y bases jurídicas</h2>
        <ul>
          <li>Funcionamiento y seguridad del Sitio: interés legítimo.</li>
          <li>
            Asociar una llamada a la campaña publicitaria de origen y facturación entre socios: interés legítimo.
          </li>
          <li>Medición de audiencia y publicidad de Google: tu consentimiento, que puedes retirar en cualquier momento.</li>
          <li>Responder a tus correos: aplicación de medidas precontractuales o interés legítimo.</li>
        </ul>

        <h2>4. Destinatarios</h2>
        <p>
          Los datos se destinan a {c.name} y a sus encargados del tratamiento: el proveedor de alojamiento del Sitio, el
          proveedor de seguimiento de llamadas y, si lo aceptas, Google (medición de audiencia y publicidad). Las
          llamadas las atiende {SITE.vertical.partner}, que actúa como responsable de sus propios tratamientos para
          los datos que le facilites por teléfono. Algunos proveedores pueden estar situados fuera del Espacio
          Económico Europeo; en ese caso, las transferencias se basan en las cláusulas contractuales tipo de la
          Comisión Europea o en una decisión de adecuación.
        </p>

        <h2>5. Plazos de conservación</h2>
        <ul>
          <li>Datos de seguimiento de llamadas: 13 meses como máximo.</li>
          <li>
            Cookies: consulta la <a href="/cookies">política de cookies</a>.
          </li>
          <li>Correos: el tiempo necesario para atender la solicitud y, después, 3 años como máximo.</li>
        </ul>

        <h2>6. Tus derechos</h2>
        <p>
          Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, oposición y portabilidad, así
          como retirar tu consentimiento en cualquier momento, escribiendo a{" "}
          <a href={"mailto:" + c.privacyEmail}>{c.privacyEmail}</a> o a la dirección postal indicada. Podremos pedirte
          una prueba de identidad en caso de duda razonable.
        </p>
        <p>
          También puedes presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).
        </p>

        <h2>7. Seguridad</h2>
        <p>
          El Sitio se sirve por HTTPS. Aplicamos medidas técnicas y organizativas adecuadas para proteger los datos y
          notificaremos cualquier brecha que suponga un riesgo elevado en los plazos legales.
        </p>

        <h2>8. Cambios</h2>
        <p>Esta política puede actualizarse. La fecha de la última actualización figura al principio de la página.</p>
      </article>
    </Layout>
  );
}

// Política de cookies (dont cookies du suivi d'appels).
export function CookiesPage() {
  useSeo({
    title: "Política de cookies",
    description: "Cookies utilizadas en " + SITE.siteLabel + " y gestión de tu consentimiento.",
    path: "/cookies",
  });

  function reset() {
    try {
      localStorage.removeItem("consentMode");
    } catch {
      // stockage indisponible
    }
    window.location.reload();
  }

  return (
    <Layout>
      <article className="wrap text-page">
        <h1>Política de cookies</h1>
        <p className="small">Última actualización: octubre de 2026</p>
        <p>
          Las cookies son pequeños archivos o datos que se guardan en tu dispositivo. Conforme al artículo 22.2 de la
          LSSI-CE, solo usamos sin tu consentimiento las cookies necesarias para el funcionamiento del Sitio y para el
          seguimiento de las llamadas. Las cookies de medición de audiencia y publicidad de Google solo se activan si
          las aceptas.
        </p>

        <h2>Cookies utilizadas</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Finalidad</th>
                <th>Duración</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>consentMode (almacenamiento local)</td>
                <td>Técnica</td>
                <td>Recordar tu elección sobre las cookies.</td>
                <td>Hasta que la borres</td>
              </tr>
              <tr>
                <td>call_session_id</td>
                <td>Seguimiento de llamadas</td>
                <td>Identificador de sesión aleatorio que permite asociar una llamada a la visita y a la campaña de origen.</td>
                <td>15 minutos</td>
              </tr>
              <tr>
                <td>odi_[número]</td>
                <td>Seguimiento de llamadas</td>
                <td>Recuerda el número de seguimiento asignado durante la visita, para mostrar el mismo número en todas las páginas.</td>
                <td>15 minutos</td>
              </tr>
              <tr>
                <td>_ga, _gcl_au y cookies de Google</td>
                <td>Analítica / publicidad</td>
                <td>Medir la audiencia y la eficacia de los anuncios de Google. Solo si las aceptas.</td>
                <td>Hasta 13 meses</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Seguimiento de llamadas</h2>
        <p>
          Cuando consultas el Sitio, los números de teléfono mostrados pueden sustituirse por un número de seguimiento.
          El identificador de sesión, el identificador de Google (_ga) si existe, la página consultada y la página de
          procedencia se transmiten a nuestro proveedor de seguimiento de llamadas para asociar la llamada a la campaña
          publicitaria de origen. Esta información no sirve para identificarte por tu nombre.
        </p>

        <h2>Gestionar tu elección</h2>
        <p>
          Puedes cambiar tu elección en cualquier momento:{" "}
          <button type="button" className="link-button" onClick={reset}>
            volver a mostrar el aviso de cookies
          </button>
          . También puedes bloquear o borrar las cookies desde la configuración de tu navegador. Para saber más sobre el
          uso de los datos por parte de Google:{" "}
          <a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer">
            business.safety.google/privacy
          </a>
          .
        </p>
      </article>
    </Layout>
  );
}
